/* eslint-disable react-hooks/rules-of-hooks */
import { motion } from 'framer-motion'
import { Howl } from 'howler'
import { uniqueId } from 'lodash'
import React, { useState } from 'react'
import AudioPlayer, { AudioPlayerVariant, variantClassMap } from './AudioPlayer'
import BotAvatar from './BotAvatar'
import BotMessageImage from '@/assets/bot-message-icon.webp'
import EllipsisAnimation from '@/components/animations/EllipsisAnimation'
import { Row, Show, Text } from '@/components/core'
import { Match, Switch } from '@/components/switch'
import { useAutoPlay } from '@/contexts/AutoPlayContext'
import { classNameBuilder } from '@/helpers'
import { useTimer } from '@/hooks/useTimer'

interface BotMessageProps {
  variant?: AudioPlayerVariant
  audioUrl?: string
  autoPlay?: boolean
  pending?: boolean
  pendingTime?: number
  playbackSpeed?: number
  recordingFeedback?: boolean
  showControls?: boolean
  message?: string
  className?: string
  isTestingAudio?: boolean
  onStartPlaying?: () => void
  onFinishPlaying?: () => void
}

const BotMessage = ({
  audioUrl,
  variant = 'primary',
  autoPlay,
  pending,
  pendingTime,
  playbackSpeed,
  recordingFeedback,
  showControls,
  message,
  className,
  isTestingAudio,
  onStartPlaying,
  onFinishPlaying
}: BotMessageProps) => {
  const { done } = useTimer({
    endTime: pendingTime || 1,
    autoStart: true
  })

  const { done: readyToPlay, start } = useTimer({
    endTime: 300
  })

  const { addToQueue, removeFromQueue } = useAutoPlay()

  const [loaded, setLoaded] = React.useState(false)
  const [testAudioPlayed, setTestAudioPlayed] = useState<boolean>(false)

  const audioRef = React.useRef<HTMLMediaElement | null>(null)
  const effectAudioInstanceRef = React.useRef<Howl | null>(null)
  const messageAudioInstanceRef = React.useRef<Howl | null>(null)

  React.useEffect(() => {
    if (recordingFeedback && !effectAudioInstanceRef.current) {
      effectAudioInstanceRef.current = new Howl({
        src: ['/audio/bot-message.mp3'],
        html5: true,
        autoplay: true,
        onloaderror: (_, error) => console.log(error),
        onplayerror: (_, error) => console.log(error)
      })
    }

    return () => {
      if (effectAudioInstanceRef.current) {
        effectAudioInstanceRef.current.unload()
      }
      if (messageAudioInstanceRef.current) {
        messageAudioInstanceRef.current.unload()
      }
      if (audioRef.current) {
        audioRef.current.remove()
      }
      if (audioUrl && removeFromQueue) {
        removeFromQueue(audioUrl)
      }
    }
  }, [])

  React.useEffect(() => {
    if (!audioRef.current && audioUrl) {
      messageAudioInstanceRef.current = new Howl({
        src: [audioUrl],
        html5: true,
        autoplay: false,
        onload: () => {
          //@ts-expect-error variable exists but is not available on ts
          audioRef.current = messageAudioInstanceRef.current._sounds[0]._node
          setLoaded(true)
        }
      })
    }
  }, [audioUrl])

  React.useEffect(() => {
    if (loaded && done && !pending) {
      start()
    }
  }, [loaded, pending, done])

  React.useEffect(() => {
    if (autoPlay && audioUrl) {
      if (isTestingAudio) {
        if (!testAudioPlayed) {
          addToQueue(audioUrl)
        }
      } else {
        addToQueue(audioUrl)
      }
    }
  }, [autoPlay, audioUrl, isTestingAudio, testAudioPlayed])

  const layoutId = uniqueId('bot-message')

  const pendingState =
    pending || !audioUrl || !loaded || !audioRef.current || !done

  const pendingBotMessagePlaceholder = (
    <motion.div
      layout
      layoutId={layoutId}
      className={classNameBuilder(
        variantClassMap[variant]?.backgroundColor,
        'flex h-14 w-full max-w-[238px] flex-row items-center justify-between rounded-2xl rounded-tl-none p-4',
        className
      )}
    >
      <Text
        size="xs"
        fontWeight="medium"
        className="px-4 !text-electric-indigo-900"
      >
        <Row className="items-center">
          Recording audio
          <EllipsisAnimation />
        </Row>
      </Text>
      <div className="min-w-fit">
        <Row className="relative h-10 w-10">
          <BotAvatar avatarIcon={BotMessageImage} />
        </Row>
      </div>
    </motion.div>
  )

  return (
    <>
      <Show
        when={!pendingState || !!message}
        fallback={pendingBotMessagePlaceholder}
      >
        <motion.div
          layout
          layoutId={layoutId}
          className={classNameBuilder(
            variantClassMap[variant]?.backgroundColor,
            'flex h-14 w-full max-w-[358px] flex-row items-center justify-between space-x-3 rounded-2xl rounded-tl-none px-4 py-3',
            className
          )}
        >
          <Switch>
            <Match when={!!audioRef.current}>
              <motion.div
                layout
                layoutId={layoutId}
                className="flex w-full flex-row"
              >
                <AudioPlayer
                  audioMedia={audioRef.current}
                  audioUrl={audioUrl}
                  uniqueAudioUrl={audioUrl}
                  variant={variant}
                  autoPlay={autoPlay && readyToPlay}
                  showControls={showControls}
                  playbackSpeed={playbackSpeed}
                  onPause={() => {
                    if (audioUrl) removeFromQueue(audioUrl)
                  }}
                  onPlay={onStartPlaying}
                  onFinish={() => {
                    if (audioUrl) {
                      removeFromQueue(audioUrl)
                    }
                    if (onFinishPlaying) {
                      onFinishPlaying()
                    }
                    if (isTestingAudio) {
                      setTestAudioPlayed(true)
                    }
                  }}
                />
              </motion.div>
            </Match>
            <Match when={!!message}>
              <Text
                size="xs"
                fontWeight="medium"
                className="px-4 !text-electric-indigo-900"
              >
                {message}
              </Text>
            </Match>
          </Switch>
          <div className="min-w-fit">
            <Row className="relative h-10 w-10">
              <BotAvatar avatarIcon={BotMessageImage} />
            </Row>
          </div>
        </motion.div>
      </Show>
    </>
  )
}

export default BotMessage
